import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import {
  adminCreateTutorRole,
  adminDeleteTutorRole,
  adminUpdateTutorRole,
  adminVerifyTutorRoleDelete,
  getTutorRolesForAdmin,
} from './adminApi';
import { RootState } from '../..';

export interface IRoleDeleteVerificationData {
  usersCount: number;
}

export const TUTOR_ROLE_PERMISSIONS = {
  COURSE: {
    VIEW: 'view',
    EDIT: 'edit',
  },
  SECTION: {
    CREATE: 'create',
    EDIT: 'edit',
    DELETE: 'delete',
  },
  TEAMS: {
    CREATE: 'create',
    EDIT: 'edit',
    DELETE: 'delete',
  },
  ZOOM: {
    CREATE: 'create',
    EDIT: 'edit',
    DELETE: 'delete',
  },
  RESOURCE: {
    CREATE: 'create',
  },
  QUIZ: {
    CREATE: 'create',
    EDIT: 'edit',
    DELETE: 'delete',
  },
} as const;

export type CoursePermissions = typeof TUTOR_ROLE_PERMISSIONS.COURSE[keyof typeof TUTOR_ROLE_PERMISSIONS.COURSE];
export type SectionPermissions = typeof TUTOR_ROLE_PERMISSIONS.SECTION[keyof typeof TUTOR_ROLE_PERMISSIONS.SECTION];
export type TeamsPermissions = typeof TUTOR_ROLE_PERMISSIONS.TEAMS[keyof typeof TUTOR_ROLE_PERMISSIONS.TEAMS];
export type ZoomPermissions = typeof TUTOR_ROLE_PERMISSIONS.ZOOM[keyof typeof TUTOR_ROLE_PERMISSIONS.ZOOM];
export type ResourcePermissions = typeof TUTOR_ROLE_PERMISSIONS.RESOURCE[keyof typeof TUTOR_ROLE_PERMISSIONS.RESOURCE];
export type QuizPermissions = typeof TUTOR_ROLE_PERMISSIONS.QUIZ[keyof typeof TUTOR_ROLE_PERMISSIONS.QUIZ];

export interface ITutorRolePermissions {
  course?: CoursePermissions[];
  section?: SectionPermissions[];
  teams?: TeamsPermissions[];
  zoom?: ZoomPermissions[];
  resource?: ResourcePermissions[];
  quiz?: QuizPermissions[];
}

export const DEFAULT_TUTOR_ROLE = 'Editor';

export interface ITutorRole {
  _id: string;
  tenant: string;
  name: string;
  permissions: ITutorRolePermissions;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
  updatedAt?: string;
}

export interface ITutorRolesListData {
  limit?: number;
  offset?: number;
  totalDocs?: number;
  list: ITutorRole[];
}

export interface IInitialTutorRolesState {
  status: 'idle' | 'loading' | 'rejected';
  adminTutorRolesListData?: ITutorRolesListData;
  adminTutorRoleDeleteVerificationData?: IRoleDeleteVerificationData;
}

const initialState: IInitialTutorRolesState = {
  status: 'idle',
  adminTutorRolesListData: undefined,
  adminTutorRoleDeleteVerificationData: undefined,
};

export const TutorRolesSlice = createSlice({
  name: 'tutorRoles',
  initialState,
  reducers: {
    resetAdminTutorRolesListData: (state) => {
      state.adminTutorRolesListData = undefined;
    },
    resetAdminTutorRoleDeleteVerificationData: (state) => {
      state.adminTutorRoleDeleteVerificationData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTutorRolesForAdmin.fulfilled, (state, action) => {
        const result = action.payload;
        state.adminTutorRolesListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };
      })
      .addCase(adminCreateTutorRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminTutorRolesListData) {
          const newAdminTutorRolesListData = {
            ...state.adminTutorRolesListData,
          };

          newAdminTutorRolesListData.list.unshift(result);

          if (newAdminTutorRolesListData.totalDocs) {
            newAdminTutorRolesListData.totalDocs += 1;
          } else {
            newAdminTutorRolesListData.totalDocs = 1;
          }

          state.adminTutorRolesListData = newAdminTutorRolesListData;
        }
      })
      .addCase(adminUpdateTutorRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminTutorRolesListData &&
          state.adminTutorRolesListData.list &&
          state.adminTutorRolesListData.list.length
        ) {
          const newAdminTutorRolesListData = {
            ...state.adminTutorRolesListData,
          };

          const index = newAdminTutorRolesListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminTutorRolesListData.list[index] = result;
          }

          state.adminTutorRolesListData = newAdminTutorRolesListData;
        }
      })
      .addCase(adminDeleteTutorRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminTutorRolesListData &&
          state.adminTutorRolesListData.list &&
          state.adminTutorRolesListData.list.length
        ) {
          const newAdminTutorRolesListData = {
            ...state.adminTutorRolesListData,
          };

          newAdminTutorRolesListData.totalDocs = newAdminTutorRolesListData.totalDocs
            ? newAdminTutorRolesListData.totalDocs - 1
            : 0;

          newAdminTutorRolesListData.list = newAdminTutorRolesListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminTutorRolesListData = newAdminTutorRolesListData;
        }
      })
      .addCase(adminVerifyTutorRoleDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminTutorRoleDeleteVerificationData = result;
      })
      .addMatcher(
        isPending(
          getTutorRolesForAdmin,
          adminCreateTutorRole,
          adminUpdateTutorRole,
          adminDeleteTutorRole,
          adminVerifyTutorRoleDelete
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isFulfilled(
          getTutorRolesForAdmin,
          adminCreateTutorRole,
          adminUpdateTutorRole,
          adminDeleteTutorRole,
          adminVerifyTutorRoleDelete
        ),
        (state) => {
          state.status = 'idle';
        }
      )
      .addMatcher(
        isRejected(
          getTutorRolesForAdmin,
          adminCreateTutorRole,
          adminUpdateTutorRole,
          adminDeleteTutorRole,
          adminVerifyTutorRoleDelete
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const getTutorRolesState = (state: RootState): RootState['tutorRoles'] =>
  state.tutorRoles;

export const {
  resetAdminTutorRolesListData,
  resetAdminTutorRoleDeleteVerificationData,
} = TutorRolesSlice.actions;

export * from './adminApi';
export default TutorRolesSlice.reducer;
